/**
 * Product Gallery UGC Tabs
 *
 * Adds a "Product Photos" / "Customer Photos" toggle to the product gallery.
 * Two layouts, chosen by the `ugc_tab_style` theme setting (read from
 * data-ugc-tab-style on the gallery root):
 *   - 'button'  : two buttons below the gallery image ([data-gallery-tabs]).
 *   - 'thumbnail'  : a single swap tile at the end of the thumbnail strip
 *                    ([data-gallery-tile]); its label flips between the
 *                    customer and product labels as you toggle.
 * When "Customer Photos" is active, UGC images display in the same full-size,
 * snap-scrolling format as the product photos.
 *
 * Tab colors are theme-driven via --ugc-tab-* custom properties (set on the
 * gallery root in product-gallery.liquid) and resolved in CSS; this module
 * only manages state, not the visual colors.
 *
 * The toggle only appears after UGC images load successfully — no layout
 * shift, no empty state flash.
 *
 * @module product-gallery-ugc
 */

import { fetchUGCImages } from '@stardust/yotpo';
import { escapeHtml, initThumbDrag } from '@stardust/utilities';
import Slider from '@stardust/slider';

export default class ProductGalleryUGC {
  /**
   * @param {HTMLElement} galleryRoot - The [data-product-gallery] element
   */
  constructor(galleryRoot) {
    this.root = galleryRoot;
    this.productId = galleryRoot.dataset.productId;
    if (!this.productId) return;

    // Toggle style + labels (set from theme settings via data-* on the root)
    this.tabStyle = galleryRoot.dataset.ugcTabStyle || 'button';
    this.productLabel = galleryRoot.dataset.ugcProductLabel || 'Product Photos';
    this.#readSettings();

    // Tab controls + image-count-dependent product refs: see #queryDomRefs.

    // UGC panel
    this.ugcPanel = galleryRoot.querySelector('[data-gallery-ugc-panel]');
    this.ugcSliderRoot = this.ugcPanel?.querySelector('[data-ugc-slider]');
    this.ugcScrollContainer = this.ugcSliderRoot?.querySelector('[data-slider-scroll-target]');
    this.ugcThumbStrip = galleryRoot.querySelector('[data-ugc-thumb-strip]');
    this.ugcThumbViewport = galleryRoot.querySelector('[data-ugc-thumb-viewport]');
    // Snapshot the server-rendered skeleton so teardown can restore it (keeps the
    // panel's height for CLS) instead of leaving the last load's photos on screen.
    this._ugcSkeletonHTML = this.ugcScrollContainer?.innerHTML ?? '';

    // State
    this.activeTab = 'product';
    this.ugcSlider = null;
    this.ugcThumbs = [];
    this.images = [];
    this._productThumbsWereVisible = false;
    this._ugcThumbDragged = false;
    this._ugcThumbDragCleanup = null;

    // Bumped on every #clearLoadedUGC so an in-flight #fetchAndBuildUGC started
    // before a morph/refresh can detect it was cancelled and bail after its await
    // instead of rendering stale photos / an untracked slider over fresh state.
    this._loadToken = 0;

    // In-flight/settled load promise, memoized so concurrent switches await the
    // same fetch instead of a boolean guard short-circuiting a second switch
    // before the thumbs exist. Null = not loaded (or a load failed/was empty).
    this._ugcLoadPromise = null;
    this._ugcReady = false;

    // Re-apply UGC visibility after section morphs (variant switches reset DOM classes)
    this._boundSectionMorph = (e) => this.#onSectionMorph(e);
    document.addEventListener('theme:section:morph', this._boundSectionMorph);

    this.#init();
  }

  // ---------------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------------

  /**
   * (Re)capture the DOM refs that depend on the current product's image
   * count. data-slider-indicators (product thumb strip) and
   * data-gallery-tile-row only render on one side of the ≤1/>1 image-count
   * boundary, and the tile carrying data-gallery-tab lives in whichever one is
   * present — a combined-listing morph that crosses that boundary replaces
   * these nodes, so re-query rather than trust refs captured before the morph.
   */
  #queryDomRefs() {
    this.productThumbs = this.root.querySelector('[data-slider-indicators]');
    this.tabBar = this.root.querySelector('[data-gallery-tabs]');
    this.tabButtons = Array.from(this.root.querySelectorAll('[data-gallery-tab]'));
    this.tileRow = this.root.querySelector('[data-gallery-tile-row]');
    // A product with no grouped images renders a second (fallback) viewport as
    // a sibling of the empty main one, and a variant morph can add/remove that
    // fallback — so re-capture every viewport here, not once in the constructor.
    this.productViewports = Array.from(this.root.querySelectorAll('[data-gallery-product-viewport]'));
  }

  async #init() {
    this.#queryDomRefs();
    if (!this.ugcPanel || !this.productId) return;

    // Snapshot the load generation like #fetchAndBuildUGC. A combined-listing
    // morph to another product bumps _loadToken (via #clearLoadedUGC) before
    // re-running #init, so a slower probe for the prior product bails after its
    // await instead of revealing a toggle over a product with no photos.
    const token = this._loadToken;
    try {
      // Lightweight probe — check whether this product has any customer photo
      // (imagesOnly so a leading video doesn't hide a gallery that does have
      // photos). Pass favoriteOnly so the probe matches what #loadUGC will
      // actually fetch: without it, an all-video favorited set (which the
      // provider keeps "favorites only", not falling back) would let the probe
      // find uncurated photos, show the toggle, then load an empty gallery.
      const { images } = await fetchUGCImages({
        productId: this.productId,
        count: 1,
        favoriteOnly: this.favoritesOnly,
        imagesOnly: true
      });
      if (token !== this._loadToken) return; // superseded by a newer morph — abandon
      if (!images.length) return;

      this.#bindTabs();
      this.#revealTabs();
      this._ugcReady = true;
    } catch (err) {
      console.error('[ProductGalleryUGC] Failed to probe UGC:', err);
    }
  }

  /**
   * Reveal the toggle controls once UGC is confirmed. Reveals whatever
   * controls actually exist in the DOM — normally that's one of tabBar
   * (button style) or tabButtons/tileRow (thumbnail style), but Tile style
   * combined with "Hide thumbnails on mobile" renders both at once (split by
   * breakpoint via CSS, see product-gallery-ugc.liquid), so this can't branch
   * on tabStyle alone. In thumbnail mode this also renders the "back to
   * product photos" tile into the (still-hidden) UGC strip — it depends only
   * on the server-rendered template, not the image set, so doing it here
   * guarantees a way back even if the later full image fetch returns nothing
   * or errors. `data-ugc-ready` turns on the right-edge fade (see
   * product-gallery-ugc.css).
   */
  #revealTabs() {
    this.root.setAttribute('data-ugc-ready', '');
    this.tabButtons.forEach(btn => btn.classList.replace('hidden', 'flex'));
    this.tileRow?.classList.replace('hidden', 'flex');
    this.tabBar?.classList.replace('hidden', 'flex');
    if (this.tabStyle === 'thumbnail') {
      this.#renderUGCTile();
    }
  }

  /**
   * Inverse of #revealTabs: re-hide the toggle and drop the cloned back-tile.
   * Used when a combined-listing morph swaps in a product we haven't confirmed
   * UGC for yet, so a stale toggle never lingers before the fresh probe.
   */
  #hideTabs() {
    this.root.removeAttribute('data-ugc-ready');
    this.tabButtons.forEach(btn => btn.classList.replace('flex', 'hidden'));
    this.tileRow?.classList.replace('flex', 'hidden');
    this.tabBar?.classList.replace('flex', 'hidden');
    this.ugcThumbStrip?.querySelector('[data-gallery-tile]')?.remove();
    this.#toggle(this.ugcThumbStrip, false);
    this.ugcPanel?.classList.add('hidden');
  }

  /**
   * Fetch full UGC image set and build the gallery. Called once on first
   * switch to the UGC tab.
   */
  #loadUGC() {
    // Memoize the in-flight promise so a quick leave-and-return to Customer
    // Photos awaits the same fetch rather than short-circuiting on a boolean
    // and sizing the thumb strip from a still-empty image set.
    if (this._ugcLoadPromise) return this._ugcLoadPromise;
    this._ugcLoadPromise = this.#fetchAndBuildUGC();
    return this._ugcLoadPromise;
  }

  async #fetchAndBuildUGC() {
    // Snapshot the load generation. If a morph/refresh clears state during the
    // await below, the token advances and this load bails rather than painting
    // stale content or leaking a slider the clear never saw.
    const token = this._loadToken;
    try {
      // imagesOnly makes maxImages a photo count: the provider pages past
      // videos so we get up to maxImages photos, not maxImages media items
      // (of which some would be videos and get dropped below).
      const { images } = await fetchUGCImages({
        productId: this.productId,
        count: this.maxImages,
        favoriteOnly: this.favoritesOnly,
        imagesOnly: true
      });
      if (token !== this._loadToken) return; // superseded by a clear — abandon
      this.images = images;
      if (!this.images.length) {
        // #init already confirmed photos exist, so an empty load is anomalous —
        // release the memo so the next tab switch retries.
        this._ugcLoadPromise = null;
        return;
      }

      this.#renderImages();
      this.#initSlider();
      this.#renderThumbs();
      // Note: the thumbnail-mode "back to product photos" tile is rendered in
      // #init (not here), so it exists even when this load yields no images.
    } catch (err) {
      if (token !== this._loadToken) return; // cleared mid-flight — don't touch state
      this._ugcLoadPromise = null; // allow retry after a transient fetch failure
      console.error('[ProductGalleryUGC] Failed to load UGC:', err);
    }
  }

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  #renderImages() {
    if (!this.ugcScrollContainer) return;
    this.ugcScrollContainer.innerHTML = this.images
      .map((img, i) => this.#renderItem(img, i))
      .join('');
  }

  /**
   * Build a full-size gallery figure matching the product gallery structure.
   *
   * @param {object} img - Normalized UGC image from yotpo provider
   * @param {number} index - Zero-based index
   * @returns {string} HTML string
   */
  #renderItem({ imageUrl, originalImageUrl, author }, index) {
    const n = index + 1;
    const authorName = author?.displayName;
    const src = originalImageUrl || imageUrl;

    let overlay = '';
    if (authorName) {
      overlay = `
        <div class="absolute bottom-0 left-0 right-0 p-4 bg-linear-to-t from-black/60 to-transparent translate-y-full group-hover:translate-y-0 transition-transform duration-300">
          <p class="text-white text-sm">${escapeHtml(authorName)}</p>
        </div>`;
    }

    return `
      <figure id="ugc-img-${n}"
        class="min-w-full snap-start snap-always scroll-mt-0 aspect-square bg-gray-100 relative group overflow-hidden"
        data-slider-item>
        <img
          src="${escapeHtml(src)}"
          alt="Customer photo${authorName ? ` by ${escapeHtml(authorName)}` : ''}"
          class="w-full h-full ${this.imageFit}"
          loading="${index === 0 ? 'eager' : 'lazy'}" />
        ${overlay}
      </figure>`;
  }

  // ---------------------------------------------------------------------------
  // Slider + Thumbnails
  // ---------------------------------------------------------------------------

  #initSlider() {
    if (!this.ugcSliderRoot) return;
    // Destroy any prior instance before replacing the ref, so a re-init can't
    // orphan a Slider (and its listeners) that nothing holds a handle to.
    this.ugcSlider?.destroy();
    this.ugcSlider = new Slider(this.ugcSliderRoot, { snap: true });
  }

  #renderThumbs() {
    if (!this.ugcThumbViewport) return;

    // Caller (#fetchAndBuildUGC) only reaches here with a non-empty image set,
    // and the map/Array.from below fully replace the viewport + thumb list — so
    // no pre-reset is needed. A single photo still renders (consistent control).
    this.ugcThumbViewport.innerHTML = this.images.map((img, i) => {
      const n = i + 1;
      return `
        <button
          type="button"
          class="shrink-0 w-20 md:w-28 aspect-square rounded-md overflow-hidden transition-all cursor-pointer focus:outline-none"
          role="tab"
          aria-label="Go to customer photo ${n}"
          aria-selected="${i === 0}"
          data-ugc-thumb="${n}"
          data-target="ugc-img-${n}">
          <img
            src="${escapeHtml(img.thumbUrl || img.imageUrl)}"
            alt="Customer photo ${n}"
            class="w-full h-full object-cover pointer-events-none"
            loading="lazy" />
        </button>`;
    }).join('');

    this.ugcThumbs = Array.from(this.ugcThumbViewport.querySelectorAll('[data-ugc-thumb]'));

    this.ugcThumbs.forEach(btn => {
      btn.addEventListener('click', () => {
        if (this._ugcThumbDragged) return;
        document.getElementById(btn.dataset.target)
          ?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      });
    });

    this.ugcSlider?.onScroll(index => this.#updateThumbs(index));

    // Detach any drag listeners from a previous load first — the viewport is a
    // persistent node, so without this each reload stacks another handler set.
    this._ugcThumbDragCleanup?.();
    this._ugcThumbDragCleanup = initThumbDrag(this.ugcThumbViewport, {
      setDragged: v => { this._ugcThumbDragged = v; },
    });
  }

  /**
   * Sync active thumb highlight to the current scroll index.
   *
   * @param {number} activeIndex - Zero-based index of the visible slide
   */
  #updateThumbs(activeIndex) {
    for (let i = 0; i < this.ugcThumbs.length; i++) {
      this.ugcThumbs[i].setAttribute('aria-selected', String(i === activeIndex));
    }
  }

  /**
   * Show/hide an element via the paired hidden/flex classes the gallery uses.
   * Keeps the two in sync so a caller can't leave the element half-toggled.
   *
   * @param {HTMLElement|null|undefined} el
   * @param {boolean} visible
   */
  #toggle(el, visible) {
    el?.classList.toggle('hidden', !visible);
    el?.classList.toggle('flex', visible);
  }

  // ---------------------------------------------------------------------------
  // Tab Switching
  // ---------------------------------------------------------------------------

  #bindTabs() {
    // Tab nodes can persist across a morph (button style) or get replaced when
    // the tile crosses the ≤1/>1 image-count boundary (thumbnail style) — tag
    // each bound node so a re-probe skips ones already wired instead of either
    // stacking duplicate listeners or (via a blanket bound flag) skipping the
    // fresh nodes #queryDomRefs just picked up.
    this.tabButtons.forEach(btn => {
      if (btn.dataset.ugcTabBound) return;
      btn.dataset.ugcTabBound = 'true';
      this.#bindTab(btn);
    });

    if (!this.tabBar || this.tabBar.dataset.ugcKeydownBound) return;
    this.tabBar.dataset.ugcKeydownBound = 'true';
    this.tabBar.addEventListener('keydown', e => {
      // Scoped to tabBar's own buttons, not this.tabButtons — the Tile-style
      // mobile fallback (see product-gallery-ugc.liquid) can put the tile in
      // that same array, and arrow-cycling into a tile hidden at the current
      // breakpoint would silently do nothing.
      const barButtons = Array.from(this.tabBar.querySelectorAll('[data-gallery-tab]'));
      const i = barButtons.indexOf(document.activeElement);
      if (i < 0) return;
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      // Own the arrow keys while a tab is focused so they don't also bubble to
      // the product gallery Slider root (an ancestor) and scroll the image track.
      e.preventDefault();
      e.stopPropagation();
      if (e.key === 'ArrowRight' && i < barButtons.length - 1) {
        barButtons[i + 1].focus();
        this.switchTab(barButtons[i + 1].dataset.galleryTab);
      } else if (e.key === 'ArrowLeft' && i > 0) {
        barButtons[i - 1].focus();
        this.switchTab(barButtons[i - 1].dataset.galleryTab);
      }
    });
  }

  /**
   * Wire a single toggle control (button or tile) to switch
   * galleries. Each control's data-gallery-tab is the tab it activates.
   *
   * @param {HTMLElement} btn
   */
  #bindTab(btn) {
    btn.addEventListener('click', () => this.switchTab(btn.dataset.galleryTab));
  }

  /**
   * Tile style only: pin a "back to product photos" tile to the
   * right of the UGC thumbnail strip (a sibling of the scrolling viewport, so
   * it stays visible while the thumbnails truncate beside it).
   *
   * Cloned from the server-rendered product-side tile so the styling has a
   * single source of truth (the Liquid markup) — then re-pointed at the product
   * tab and relabelled as the "back" tile.
   */
  #renderUGCTile() {
    if (!this.ugcThumbStrip) return;
    if (this.ugcThumbStrip.querySelector('[data-gallery-tile]')) return;

    const template = this.root.querySelector('[data-gallery-tile]');
    if (!template) return;

    const tile = template.cloneNode(true);
    tile.dataset.galleryTab = 'product';
    tile.setAttribute('aria-label', this.productLabel);
    tile.textContent = this.productLabel;
    tile.classList.remove('hidden');
    tile.classList.add('flex');

    this.ugcThumbStrip.appendChild(tile);
    // Flag it bound here — this clone carries data-gallery-tab, so a later
    // #bindTabs re-probe would otherwise re-query and double-bind it.
    tile.dataset.ugcTabBound = 'true';
    this.#bindTab(tile);
  }

  /**
   * Switch between product and UGC gallery views.
   *
   * @param {'product'|'ugc'} tab
   */
  async switchTab(tab) {
    if (tab === this.activeTab) return;
    this.activeTab = tab;
    const isUGC = tab === 'ugc';

    // Button control: reflect active/inactive state (CSS keys colors off
    // [aria-selected]). The tile has no active/inactive state. Checked via
    // tabBar's existence, not this.tabStyle === 'button' — Tile style plus
    // "Hide thumbnails on mobile" renders this same bar as the phone-side
    // fallback (see product-gallery-ugc.liquid), so it needs this too even
    // though tabStyle stays 'thumbnail'. Scoped to tabBar's own buttons so the
    // tile (which can coexist with the bar in that fallback case) never gets
    // an aria-selected it has no use for.
    if (this.tabBar) {
      for (const btn of this.tabBar.querySelectorAll('[data-gallery-tab]')) {
        const isActive = btn.dataset.galleryTab === tab;
        btn.setAttribute('aria-selected', String(isActive));
        btn.tabIndex = isActive ? 0 : -1;
      }
    }

    // Snapshot product thumbs visibility before hiding
    if (isUGC) {
      this._productThumbsWereVisible = this.productThumbs
        ? !this.productThumbs.classList.contains('hidden')
        : false;
    }

    // Toggle product elements. The prev/next arrows live inside the viewport,
    // so hiding it hides them too — don't set inline display on the arrows or
    // it overrides the product Slider's class-based control and can strand an
    // arrow visible on a single-image gallery after returning from UGC.
    this.productViewports.forEach(v => v.classList.toggle('hidden', isUGC));

    if (this._productThumbsWereVisible) {
      this.#toggle(this.productThumbs, !isUGC);
    }

    // Thumbnail mode, ≤1-image products: the "Customer Photos" tile lives in its
    // own row (no thumb strip to host it). That's a product-side control, so hide
    // it under UGC — the "back to product photos" tile in the UGC strip is the
    // control there. Null in button style / multi-image products (no-op).
    this.#toggle(this.tileRow, !isUGC);

    // Reveal the UGC panel up front so its skeleton is visible while images
    // fetch (CLS-safe placeholder); hide it immediately when returning.
    this.ugcPanel?.classList.toggle('hidden', !isUGC);

    // Thumbnail mode: reveal the UGC strip up front too — it hosts the cloned
    // "back to product photos" tile (rendered in #revealTabs), and the product-
    // side tile is already hidden above. Without this, a slow/hung fetch strands
    // the shopper on the skeleton with no way back during the await. Button mode
    // keeps its tabs visible throughout, so it already has a back control.
    if (isUGC && this.tabStyle === 'thumbnail') {
      this.#toggle(this.ugcThumbStrip, true);
    }

    // Fetch images on first UGC switch — skeleton shows during the await. Bail
    // if the user toggled back to product before the fetch resolved, so we
    // don't re-reveal UGC over a now-active product view.
    if (isUGC) {
      await this.#loadUGC();
      if (this.activeTab !== tab) return;
      if (!this.images.length) {
        // The probe promised photos but the full fetch errored or came back
        // empty — don't strand the shopper on an endless skeleton. Snap back to
        // product photos; #fetchAndBuildUGC already released the memo so a later
        // switch retries the fetch.
        this.switchTab('product');
        return;
      }
    }

    // Toggle the UGC thumb strip. In thumbnail mode the strip must show even
    // with ≤1 UGC thumb, because it hosts the "back to product photos" tile.
    const showUgcStrip = isUGC && (this.ugcThumbs.length > 0 || this.tabStyle === 'thumbnail');
    this.#toggle(this.ugcThumbStrip, showUgcStrip);

    // Re-evaluate arrow disabled state after layout settles
    if (isUGC) {
      requestAnimationFrame(() => this.ugcSlider?.updateControls());
    }
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  /**
   * Re-sync after a section morph. Morphdom resets server-rendered classes,
   * wiping any JS-added visibility. The tab event listeners survive (same DOM
   * nodes), but the UGC slider and loaded images are gone.
   *
   * A combined-listing morph can also swap a *different* product onto the same
   * gallery root, so re-read data-product-id first: if it changed, the prior
   * probe result no longer applies — tear the toggle down and re-probe from
   * scratch rather than showing the previous product's photos (or a toggle for
   * a product that has none).
   */
  async #onSectionMorph(event) {
    // The event bubbles to document from whichever section re-rendered. A
    // combined-listing switch morphs *every* #MainContent section, so ignore
    // morphs that don't contain this gallery — their morphdom pass never touched
    // our DOM classes, and re-syncing anyway strands the UGC skeleton over a
    // still-hidden product view.
    if (!event.target.contains(this.root)) return;

    const newProductId = this.root.dataset.productId;

    if (newProductId !== this.productId) {
      this.productId = newProductId;
      this._ugcReady = false;
      // #resetToProductTab re-queries the boundary-dependent nodes so #hideTabs
      // targets the current product's tab/thumb nodes, not the prior product's
      // now-detached refs. #init re-probes and re-reveals from scratch.
      this.#resetToProductTab();
      this.#hideTabs();
      await this.#init();
      return;
    }

    if (!this._ugcReady) return;

    // Same product, but a variant morph can still cross the ≤1/>1 image-count
    // boundary and replace the tile / thumb strip / viewport nodes — so re-query
    // and re-bind before revealing, or reveal/bind would target detached nodes
    // (control stays hidden, new tiles unwired, product images leak under UGC).
    this.#resetToProductTab();
    this.#bindTabs();
    this.#revealTabs();
  }

  /**
   * Shared morph reset: drop loaded UGC, snap back to the product tab, and
   * re-capture the image-count-dependent DOM refs. Both morph paths start here;
   * they diverge on whether to re-probe (different product) or re-reveal (same).
   */
  #resetToProductTab() {
    this.#clearLoadedUGC();
    this.activeTab = 'product';
    this._productThumbsWereVisible = false;
    this.#queryDomRefs();
  }

  /**
   * Drop the loaded UGC slider/images so the gallery re-fetches from scratch
   * on the next load. Leaves the active tab untouched — callers decide that.
   */
  #clearLoadedUGC() {
    // Invalidate any in-flight load so it bails after its await instead of
    // rendering into the state we're about to reset.
    this._loadToken++;
    this.ugcSlider?.destroy();
    this.ugcSlider = null;
    this._ugcThumbDragCleanup?.();
    this._ugcThumbDragCleanup = null;
    this._ugcLoadPromise = null;
    this.ugcThumbs = [];
    this.images = [];

    // Drop rendered markup back to the skeleton + empty thumbs. Without this, a
    // reload that yields no images (maxImages 0, or favorites filtering to empty)
    // returns early from #fetchAndBuildUGC and the previous photos stay on screen.
    if (this.ugcScrollContainer) this.ugcScrollContainer.innerHTML = this._ugcSkeletonHTML;
    if (this.ugcThumbViewport) this.ugcThumbViewport.innerHTML = '';
  }

  /** Read maxImages/favoritesOnly/imageFit from the root's dataset. */
  #readSettings() {
    // Default 5 matches the Liquid default; NaN-guard the parse in case the
    // attribute is missing or malformed (schema enforces a minimum of 1).
    const parsedMax = parseInt(this.root.dataset.ugcMaxImages, 10);
    this.maxImages = Number.isNaN(parsedMax) ? 5 : parsedMax;
    this.favoritesOnly = this.root.dataset.ugcFavoritesOnly === 'true';
    // Tailwind object-fit class for the full-size photos; thumbnails stay cover.
    this.imageFit = this.root.dataset.ugcImageFit === 'contain' ? 'object-contain' : 'object-cover';
  }

  /**
   * Re-read maxImages/favoritesOnly and re-fetch. Used by the theme-editor
   * auto-init below when the customizer's live preview updates settings on
   * this same, persisting DOM node — the constructor only reads these once, so
   * without this they'd stay frozen at whatever they were on first load.
   *
   * No-op when the settings are unchanged (the customizer fires on every edit,
   * most unrelated). If the Customer Photos tab is open, reload it in place so
   * the change shows immediately rather than only on the next tab switch.
   */
  refreshSettings() {
    const prevMax = this.maxImages;
    const prevFavorites = this.favoritesOnly;
    const prevFit = this.imageFit;
    this.#readSettings();
    if (!this._ugcReady) return;
    if (this.maxImages === prevMax && this.favoritesOnly === prevFavorites && this.imageFit === prevFit) return;

    this.#clearLoadedUGC();
    if (this.activeTab === 'ugc') this.#loadUGC();
  }

  destroy() {
    this.#clearLoadedUGC();
    document.removeEventListener('theme:section:morph', this._boundSectionMorph);
  }
}

// ---------------------------------------------------------------------------
// Auto-init
// ---------------------------------------------------------------------------

let current = null;

/**
 * Attach (or refresh) the ProductGalleryUGC instance for the given root.
 * If it's the same node we already own (the theme editor's live preview morphs
 * settings onto the persisting node), just refresh its settings; if the node
 * was replaced, tear down the old instance and build a fresh one.
 *
 * @param {HTMLElement} el
 */
function attach(el) {
  if (current?.root === el) {
    current.refreshSettings();
    return;
  }
  current?.destroy();
  current = new ProductGalleryUGC(el);
}

function _init() {
  const el = document.querySelector('[data-product-gallery][data-product-id]');
  if (el) attach(el);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', _init);
} else {
  _init();
}

// Theme editor: maxImages/favoritesOnly/labels are read once at construction
// time — re-run init whenever the customizer's live preview reloads a
// section so they don't stay frozen at whatever they were on first load.
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', _init);
}
